<template>
	<div id="clientDirectoryList" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + $route.params.id" :title="client ? client.name : ''" :subtitle="cfg.title">
			<a v-if="heygovUrl && source === 'heygov'" class="hu-pill" :href="heygovUrl + (isDepartments ? '/departments' : '/people')" target="_blank" rel="noopener">
				<font-awesome-icon :icon="['fas', 'external-link-alt']" />
				<span>{{ isDepartments ? 'HeyGov departments' : 'HeyGov CRM' }}</span>
			</a>
			<button class="hu-pill" @click="load()" title="Reload" :disabled="loading">
				<font-awesome-icon :icon="['fas', 'sync-alt']" :spin="loading" />
				<span>Reload</span>
			</button>
		</JuniperPageHeader>

		<div class="jrd-intro mt-3">
			<span>{{ intro }}</span>
			<div v-if="hint" class="jrd-intro__hint">
				<font-awesome-icon class="text-muted me-1" :icon="['fas', 'info-circle']" />
				{{ hint }}
			</div>
		</div>

		<div class="cp-toolbar mt-3">
			<!-- Files: HeyGov (origin hg, mirrored from heygov-api) vs Juniper uploads -->
			<div v-if="isFiles && showOriginTabs" class="act-pills" role="group" aria-label="File source">
				<button
					v-for="o in originTabs"
					:key="o.key"
					type="button"
					class="act-pill"
					:class="{ active: origin === o.key }"
					:aria-pressed="origin === o.key ? 'true' : 'false'"
					@click="setOrigin(o.key)"
				>
					{{ o.label }}<span class="cp-tab__count">{{ totals[o.key].files }} · {{ formatBytes(totals[o.key].bytes) }}</span>
				</button>
			</div>
			<div v-else-if="isFiles" class="cp-live-count"><b>{{ total }}</b> files · {{ formatBytes(totals[origin] ? totals[origin].bytes : 0) }}</div>
			<div v-else class="cp-live-count"><b>{{ heygovLists || paged ? total : visibleRows.length }}</b> {{ heygovLists ? 'public lists' : cfg.many }}</div>

			<div v-if="isFiles" class="cp-filters">
				<select v-model="mediaType" class="cp-input" aria-label="Filter by type" @change="onFilter">
					<option value="">All types</option>
					<option value="image">Images</option>
					<option value="application">Documents</option>
					<option value="video">Video</option>
					<option value="audio">Audio</option>
				</select>
				<input v-model="search" type="search" class="cp-input cp-input--search" :placeholder="cfg.search" aria-label="Search" @input="onSearch" />
			</div>
			<div v-else-if="!heygovLists" class="cp-filters">
				<select v-if="isDepartments && source === 'heygov' && typeOptions.length > 1" v-model="type" class="cp-input" aria-label="Filter by type">
					<option value="">All types</option>
					<option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
				</select>
				<select v-model="status" class="cp-input" aria-label="Filter by status" @change="onFilter">
					<option value="">All statuses</option>
					<option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
				</select>
				<input v-model="search" type="search" class="cp-input cp-input--search" :placeholder="cfg.search" aria-label="Search" @input="onSearch" />
			</div>
		</div>

		<div class="ada-card mt-2">
			<div v-if="loading && !rows.length" class="act-empty">
				<span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading…
			</div>
			<div v-else-if="error" class="act-empty text-danger">{{ error }}</div>
			<div v-else-if="!visibleRows.length" class="act-empty">{{ emptyText }}</div>
			<div v-else class="act-table-wrap" :class="{ 'cp-dim': loading }">
				<!-- Departments (HeyGov or Juniper) -->
				<table v-if="isDepartments" class="act-table">
					<thead>
						<tr>
							<th>Name</th>
							<th v-if="source === 'heygov'">Type</th>
							<th>Status</th>
							<th>On website</th>
							<th v-if="source === 'heygov'">Contact</th>
							<th>Pages</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="row in pageRows">
						<tr :key="row.id" :class="{ 'cp-row--muted': row.status === 'archived' }">
							<td class="cp-title">{{ row.title || '(no name)' }}</td>
							<td v-if="source === 'heygov'" class="cp-muted cp-cap">{{ row.type }}</td>
							<td><span :class="['cp-pill', 'cp-pill--' + statusColor(row.status)]">{{ statusLabel(row.status) }}</span></td>
							<td>
								<span v-if="row.on_website" class="cp-yes"><font-awesome-icon :icon="['fas', 'check']" /> Yes</span>
								<span v-else class="cp-muted">No</span>
							</td>
							<td v-if="source === 'heygov'" class="cp-muted cp-contact">
								<div v-if="row.email">{{ row.email }}</div>
								<div v-if="row.phone">{{ row.phone }}</div>
								<span v-if="!row.email && !row.phone">—</span>
							</td>
							<td class="cp-pages">
								<!-- unified: the department's own Page Builder page (its website URL) -->
								<a v-if="row.department_page && row.department_page.url" class="cp-page-link" :href="row.department_page.url" target="_blank" rel="noopener" title="Department page on the website">
									<font-awesome-icon :icon="['fas', 'file-alt']" /> Department page
								</a>
								<span v-else-if="row.department_page" class="cp-page-link cp-page-link--off" :title="'Department page is ' + row.department_page.status">
									<font-awesome-icon :icon="['fas', 'file-alt']" /> Department page <span :class="['cp-pill', 'cp-pill--' + statusColor(row.department_page.status)]">{{ statusLabel(row.department_page.status) }}</span>
								</span>
								<button
									v-if="row.pages && row.pages.length"
									type="button"
									class="cp-pages-toggle"
									:aria-expanded="expanded.includes(row.id) ? 'true' : 'false'"
									@click="toggle(row.id)"
								>
									{{ row.pages.length }} page{{ row.pages.length === 1 ? '' : 's' }}
									<font-awesome-icon :icon="['fas', expanded.includes(row.id) ? 'chevron-up' : 'chevron-down']" />
								</button>
								<span v-if="!row.department_page && !(row.pages && row.pages.length)" class="cp-muted">—</span>
							</td>
							<td class="text-end act-actions">
								<a v-if="row.url" class="act-row-btn" :href="row.url" target="_blank" rel="noopener" title="View on website">
									<font-awesome-icon :icon="['fas', 'globe']" />
								</a>
								<a v-if="source === 'heygov' && heygovUrl" class="act-row-btn" :href="heygovUrl + '/departments/' + row.id" target="_blank" rel="noopener" title="Open in HeyGov">
									<font-awesome-icon :icon="['fas', 'external-link-alt']" />
								</a>
							</td>
						</tr>
						<tr v-if="expanded.includes(row.id)" :key="row.id + '-pages'" class="cp-subrow">
							<td :colspan="source === 'heygov' ? 7 : 5">
								<ul class="cp-page-list">
									<li v-for="page in row.pages" :key="page.id">
										<a v-if="page.url" :href="page.url" target="_blank" rel="noopener">{{ page.title || '(no title)' }}</a>
										<span v-else>{{ page.title || '(no title)' }}</span>
										<span :class="['cp-pill', 'cp-pill--' + statusColor(page.status)]">{{ statusLabel(page.status) }}</span>
										<span v-if="page.url" class="cp-page-url">{{ shortUrl(page.url) }}</span>
									</li>
								</ul>
							</td>
						</tr>
						</template>
					</tbody>
				</table>

				<!-- Business directory (Juniper) -->
				<table v-else-if="type_ === 'business'" class="act-table">
					<thead>
						<tr>
							<th>Business</th>
							<th>Category</th>
							<th>Contact</th>
							<th>Status</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in pageRows" :key="row.id">
							<td>
								<div class="cp-title">{{ row.title || '(no name)' }}</div>
								<div v-if="row.address" class="cp-sub">{{ row.address }}</div>
							</td>
							<td class="cp-muted">{{ row.categories && row.categories.length ? row.categories.join(', ') : '—' }}</td>
							<td class="cp-muted cp-contact">
								<div v-if="row.email">{{ row.email }}</div>
								<div v-if="row.phone">{{ row.phone }}</div>
								<span v-if="!row.email && !row.phone">—</span>
							</td>
							<td><span :class="['cp-pill', 'cp-pill--' + statusColor(row.status)]">{{ statusLabel(row.status) }}</span></td>
							<td class="text-end act-actions">
								<a v-if="row.url" class="act-row-btn" :href="row.url" target="_blank" rel="noopener" title="View on website">
									<font-awesome-icon :icon="['fas', 'globe']" />
								</a>
								<a v-if="row.website" class="act-row-btn" :href="row.website" target="_blank" rel="noopener nofollow" title="Business website">
									<font-awesome-icon :icon="['fas', 'external-link-alt']" />
								</a>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- Files (media library) -->
				<table v-else-if="isFiles" class="act-table">
					<thead>
						<tr>
							<th class="cp-sortable" @click="sortBy('name')">File <font-awesome-icon v-if="orderby === 'name'" :icon="['fas', order === 'asc' ? 'sort-up' : 'sort-down']" /></th>
							<th>Type</th>
							<th class="cp-sortable" @click="sortBy('size')">Size <font-awesome-icon v-if="orderby === 'size'" :icon="['fas', order === 'asc' ? 'sort-up' : 'sort-down']" /></th>
							<th class="cp-sortable" @click="sortBy('date')">Uploaded <font-awesome-icon v-if="orderby === 'date'" :icon="['fas', order === 'asc' ? 'sort-up' : 'sort-down']" /></th>
							<th>Used in</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in pageRows" :key="row.id">
							<td class="cp-file">
								<a v-if="row.url" :href="row.url" target="_blank" rel="noopener" class="cp-file__name">{{ row.title }}</a>
								<span v-else class="cp-file__name">{{ row.title }}</span>
							</td>
							<td class="cp-muted cp-nowrap">{{ fileType(row) }}</td>
							<td class="cp-muted cp-nowrap">{{ formatBytes(row.size) }}</td>
							<td class="cp-date">{{ formatDate(row.created_at) }}</td>
							<td class="cp-usage">
								<template v-if="row.used_in.length">
									<span v-for="(u, i) in row.used_in.slice(0, 3)" :key="u.type + u.id + u.via" class="cp-usage__item">
										<span class="cp-usage__type">{{ usageLabel(u) }}</span>
										<a v-if="u.url" :href="u.url" target="_blank" rel="noopener">{{ u.title || '#' + u.id }}</a>
										<span v-else>{{ u.title || '#' + u.id }}</span><template v-if="i < Math.min(row.used_in.length, 3) - 1">, </template>
									</span>
									<span v-if="row.used_in.length > 3" class="cp-muted"> +{{ row.used_in.length - 3 }} more</span>
								</template>
								<span v-else class="cp-muted" title="Not found in pages, news, FAQs or business listings — may still be used elsewhere (menus, widgets, departments, theme)">Not found</span>
							</td>
							<td class="text-end act-actions">
								<a v-if="row.heygov_url" class="act-row-btn" :href="row.heygov_url" target="_blank" rel="noopener" :title="row.heygov_url.includes('?q=') ? 'Search in HeyGov media library' : 'Open in HeyGov media library'">
									<font-awesome-icon :icon="['fas', 'external-link-alt']" />
								</a>
								<a v-if="row.url" class="act-row-btn" :href="row.url" target="_blank" rel="noopener" title="Open file">
									<font-awesome-icon :icon="['fas', 'download']" />
								</a>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- HeyGov subscriber lists -->
				<table v-else-if="heygovLists" class="act-table">
					<thead>
						<tr>
							<th>List</th>
							<th>Visibility</th>
							<th>Created</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in pageRows" :key="row.id">
							<td>
								<div class="cp-title">{{ row.title }}</div>
								<div v-if="row.description" class="cp-sub">{{ row.description }}</div>
							</td>
							<td class="cp-muted cp-cap">{{ row.visibility }}</td>
							<td class="cp-date">{{ formatDate(row.created_at) }}</td>
							<td class="text-end act-actions">
								<a v-if="heygovUrl" class="act-row-btn" :href="heygovUrl + '/people'" target="_blank" rel="noopener" title="Open subscribers in HeyGov CRM">
									<font-awesome-icon :icon="['fas', 'external-link-alt']" />
								</a>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- Juniper subscribers -->
				<table v-else class="act-table">
					<thead>
						<tr>
							<th>Name</th>
							<th>Email</th>
							<th>Phone</th>
							<th>Receives</th>
							<th>Status</th>
							<th>Subscribed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in pageRows" :key="row.id">
							<td class="cp-title">{{ row.title || '—' }}</td>
							<td class="cp-muted">{{ row.email || '—' }}</td>
							<td class="cp-muted cp-nowrap">{{ row.phone || '—' }}</td>
							<td>
								<span v-for="c in row.channels" :key="c" class="cp-pill cp-pill--blue">{{ c === 'sms' ? 'SMS' : 'Email' }}</span>
								<span v-if="!row.channels.length" class="cp-muted">—</span>
							</td>
							<td><span :class="['cp-pill', 'cp-pill--' + statusColor(row.status)]">{{ statusLabel(row.status) }}</span></td>
							<td class="cp-date">{{ formatDate(row.created_at) }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Same footer as ClientContentList: range + rows-per-page popover, page links.
				Subscribers/files page on the server, the other lists here. -->
			<div v-if="listTotal" class="act-pagination">
				<div ref="perPageWrap" class="cp-perpage">
					<button
						type="button"
						class="cp-perpage__toggle"
						aria-haspopup="true"
						:aria-expanded="perPageOpen ? 'true' : 'false'"
						title="Rows per page"
						:aria-label="rangeLabel + ', rows per page'"
						@click="perPageOpen = !perPageOpen"
					>
						{{ rangeLabel }}
						<font-awesome-icon :icon="['fas', 'chevron-down']" class="cp-perpage__caret" />
					</button>
					<div v-if="perPageOpen" class="cp-perpage__menu" role="menu" @keydown.esc="perPageOpen = false">
						<div class="cp-perpage__title">Rows per page</div>
						<button
							v-for="opt in perPageOptions"
							:key="opt"
							type="button"
							role="menuitemradio"
							:aria-checked="perPage === opt ? 'true' : 'false'"
							:class="['cp-perpage__opt', { active: perPage === opt }]"
							@click="setPerPage(opt)"
						>
							{{ opt }}
							<font-awesome-icon v-if="perPage === opt" :icon="['fas', 'check']" />
						</button>
					</div>
				</div>
				<div v-if="lastPage > 1" class="act-pagination__nav">
					<button type="button" class="act-page-link act-page-link--arrow" :disabled="loading || page <= 1" title="Previous page" aria-label="Previous page" @click="goTo(page - 1)">
						<font-awesome-icon :icon="['fas', 'chevron-left']" />
					</button>
					<template v-for="(p, i) in pageLinks">
						<span v-if="p === '...'" :key="'dots-' + i" class="act-page-dots">…</span>
						<button
							v-else
							:key="'p-' + p"
							type="button"
							class="act-page-link"
							:class="{ active: p === page }"
							:aria-current="p === page ? 'page' : null"
							:disabled="loading"
							@click="goTo(p)"
						>
							{{ p }}
						</button>
					</template>
					<button type="button" class="act-page-link act-page-link--arrow" :disabled="loading || page >= lastPage" title="Next page" aria-label="Next page" @click="goTo(page + 1)">
						<font-awesome-icon :icon="['fas', 'chevron-right']" />
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'

// heygov-api / the juniper endpoints cap a page at 200, so no "All"
const PER_PAGE_OPTIONS = [10, 20, 50, 100, 200]

function readPerPage(type) {
	try {
		const saved = parseInt(localStorage.getItem('mc2.juniperClientDirectory.' + type + '.perPage'), 10)
		if (PER_PAGE_OPTIONS.includes(saved)) return saved
	} catch (e) {
		// storage blocked — fall back to the default
	}
	return 20
}

const TYPES = {
	departments: { title: 'Departments', many: 'departments', search: 'Search name…' },
	subscribers: { title: 'Subscribers', many: 'subscribers', search: 'Search name, email, phone…' },
	business: { title: 'Business Directory', many: 'businesses', search: 'Search name, email…' },
	files: { title: 'Files', many: 'files', search: 'Search file name…' },
}

/**
 * Read-only Departments / Subscribers / Business directory / Files screens behind
 * the Client edit chips (Landings go to the existing ClientLandingPages)
 * (route meta.directoryType). juniper-api ClientDirectoryController picks the
 * source from the client's dashboard: Juniper tables for town-web sites,
 * HeyGov for unified ones. Department rows carry their Page Builder pages
 * (department page + attached pages). HeyGov gives no subscriber people to an
 * API key, so unified "subscribers" are the public HeyGov lists.
 */
export default {
	name: 'ClientDirectoryList',
	data() {
		return {
			client: null,
			rows: [],
			total: 0,
			expanded: [],
			// files
			origin: '',
			totals: { hg: { files: 0, bytes: 0 }, tw: { files: 0, bytes: 0 } },
			mediaType: '',
			orderby: 'date',
			order: 'desc',
			enabled: undefined,
			dashboard: '',
			source: '',
			heygovUrl: '',
			error: '',
			loading: false,
			search: '',
			status: '',
			type: '',
			page: 1,
			perPage: readPerPage(this.$route.meta.directoryType),
			perPageOptions: PER_PAGE_OPTIONS,
			perPageOpen: false,
			searchTimer: null,
		}
	},
	computed: {
		// departments | subscribers | business | files (route meta.directoryType)
		type_() {
			return this.$route.meta.directoryType
		},
		cfg() {
			return TYPES[this.type_] || TYPES.departments
		},
		isDepartments() {
			return this.type_ === 'departments'
		},
		isSubscribers() {
			return this.type_ === 'subscribers'
		},
		isFiles() {
			return this.type_ === 'files'
		},
		originTabs() {
			return [
				{ key: 'hg', label: 'HeyGov' },
				{ key: 'tw', label: 'Juniper' },
			]
		},
		// both sources hold files → tabs; otherwise just the one list
		showOriginTabs() {
			return this.totals.hg.files > 0 && this.totals.tw.files > 0
		},
		heygovLists() {
			return this.isSubscribers && this.source === 'heygov'
		},
		// Juniper subscribers page on the server; everything else filters here
		paged() {
			return (this.isSubscribers && this.source === 'juniper') || this.isFiles
		},
		// rows behind the footer: server total when paged, filtered rows otherwise
		listTotal() {
			return this.paged ? this.total : this.visibleRows.length
		},
		pageRows() {
			if (this.paged) return this.rows
			return this.visibleRows.slice((this.page - 1) * this.perPage, this.page * this.perPage)
		},
		lastPage() {
			return Math.max(1, Math.ceil(this.listTotal / this.perPage))
		},
		rangeLabel() {
			const from = (this.page - 1) * this.perPage + 1
			const to = Math.min(this.page * this.perPage, this.listTotal)
			return `${from}–${to} of ${this.listTotal}`
		},
		// windowed page list with ellipsis, same as ClientContentList: 1 … 4 5 6 7 8 … 20
		pageLinks() {
			const out = []
			let prev = 0
			for (let i = 1; i <= this.lastPage; i++) {
				if (i === 1 || i === this.lastPage || (i >= this.page - 2 && i <= this.page + 2)) {
					if (prev && i - prev > 1) out.push('...')
					out.push(i)
					prev = i
				}
			}
			return out
		},
		statusOptions() {
			if (this.isDepartments && this.source === 'heygov') {
				return [
					{ value: 'active', label: 'Active' },
					{ value: 'archived', label: 'Archived' },
				]
			}
			return [
				{ value: 'publish', label: 'Published' },
				{ value: 'draft', label: 'Draft' },
			]
		},
		typeOptions() {
			return [...new Set(this.rows.map(r => r.type).filter(Boolean))].sort()
		},
		visibleRows() {
			if (this.paged || this.heygovLists) return this.rows
			const q = this.search.trim().toLowerCase()
			return this.rows.filter(
				r =>
					(!this.status || r.status === this.status) &&
					(!this.type || r.type === this.type) &&
					(!q || (r.title || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q))
			)
		},
		intro() {
			if (this.type_ === 'business') return 'Business directory listings of this website.'
			if (this.isFiles) return 'Media library of this website. HeyGov files are uploaded in HeyGov and stored on files.heygov.com; Juniper files were uploaded through the Juniper media library.'
			if (this.isDepartments) {
				return this.source === 'heygov'
					? 'Departments, boards, commissions and committees of this unified site, as set up in HeyGov.'
					: 'Departments stored in Juniper (town-web dashboard).'
			}
			return this.source === 'heygov'
				? 'Subscriber lists of this unified site in HeyGov. The website subscribe form adds people to these lists.'
				: 'Subscribers stored in Juniper (town-web dashboard subscribe form).'
		},
		hint() {
			if (this.isFiles) return '"Used in" searches pages, news, FAQs and business listings. "Not found" files may still be used in menus, widgets, departments or the theme.'
			if (this.heygovLists) return 'Only public lists are shown, without members — HeyGov shares list members and counts only with signed-in staff. Open HeyGov CRM to see the people.'
			if (this.isDepartments && this.source === 'heygov') return 'Read-only. Edit departments in HeyGov.'
			if (this.type_ === 'business' && this.enabled === false) return 'The business directory is turned off for this site — listings are not on the website.'
			return ''
		},
		emptyText() {
			if (this.heygovLists) return 'No public subscriber lists in HeyGov.'
			return `No ${this.cfg.many} found.`
		},
	},
	watch: {
		// departments ↔ subscribers reuse this instance
		'$route.meta.directoryType'(type) {
			this.enabled = undefined
			this.perPage = readPerPage(type)
			this.resetFilters()
			this.load()
		},
		// client-side lists: a new filter starts at page 1 (paged ones reload in onFilter/onSearch)
		type() {
			if (!this.paged) this.page = 1
		},
		status() {
			if (!this.paged) this.page = 1
		},
		search() {
			if (!this.paged) this.page = 1
		},
	},
	created() {
		juniperApi
			.get('/clients/' + this.$route.params.id)
			.then(({ data }) => (this.client = data))
			.catch(error => console.log(error))
		this.load()
	},
	mounted() {
		document.addEventListener('mousedown', this.onOutsideClick)
	},
	beforeDestroy() {
		clearTimeout(this.searchTimer)
		document.removeEventListener('mousedown', this.onOutsideClick)
	},
	methods: {
		resetFilters() {
			this.origin = ''
			this.mediaType = ''
			this.orderby = 'date'
			this.order = 'desc'
			this.search = ''
			this.status = ''
			this.type = ''
			this.page = 1
			this.rows = []
			this.expanded = []
		},
		load() {
			this.loading = true
			this.error = ''
			const params = {}
			// subscribers always page on the server (HeyGov lists ignore it)
			if (this.isFiles) {
				params.page = this.page
				params.per_page = this.perPage
				params.orderby = this.orderby
				params.order = this.order
				if (this.origin) params.origin = this.origin
				if (this.mediaType) params.media_type = this.mediaType
				if (this.search.trim()) params.search = this.search.trim()
			} else if (this.isSubscribers) {
				params.page = this.page
				params.per_page = this.perPage
				if (this.status) params.status = this.status
				if (this.search.trim()) params.search = this.search.trim()
			}
			const path = `/clients/${this.$route.params.id}/directory/${this.type_}`
			return juniperApi
				.get(path, { params })
				.then(({ data }) => this.apply(data))
				.catch(error => {
					const data = (error.response && error.response.data) || {}
					this.apply({ ...data, rows: [] })
					this.error = data.message || error.message
				})
				.finally(() => (this.loading = false))
		},
		apply(data) {
			this.dashboard = data.dashboard || this.dashboard
			this.source = data.source || this.source
			this.heygovUrl = data.heygov_url || ''
			this.enabled = data.enabled
			this.rows = data.rows || []
			this.total = data.total !== undefined ? data.total : this.rows.length
			if (data.totals) this.totals = data.totals
			if (data.origin) this.origin = data.origin
		},
		toggle(id) {
			this.expanded = this.expanded.includes(id) ? this.expanded.filter(x => x !== id) : [...this.expanded, id]
		},
		shortUrl(url) {
			return url.replace(/^https?:\/\/[^/]+/, '')
		},
		onFilter() {
			if (!this.paged) return
			this.page = 1
			this.load()
		},
		onSearch() {
			if (!this.paged) return
			clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(() => {
				this.page = 1
				this.load()
			}, 350)
		},
		setOrigin(origin) {
			if (this.origin === origin) return
			this.origin = origin
			this.page = 1
			this.load()
		},
		sortBy(column) {
			if (this.orderby === column) {
				this.order = this.order === 'asc' ? 'desc' : 'asc'
			} else {
				this.orderby = column
				this.order = column === 'name' ? 'asc' : 'desc'
			}
			this.page = 1
			this.load()
		},
		formatBytes(bytes) {
			const n = Number(bytes) || 0
			if (n < 1024) return n + ' B'
			const units = ['KB', 'MB', 'GB', 'TB']
			let v = n / 1024
			let i = 0
			while (v >= 1024 && i < units.length - 1) {
				v /= 1024
				i++
			}
			return (v >= 100 ? Math.round(v) : v.toFixed(1)) + ' ' + units[i]
		},
		fileType(row) {
			const ext = (row.title || '').split('.').pop()
			if (row.mime_type === 'application/pdf') return 'PDF'
			if (row.media_type === 'image') return 'Image' + (ext ? ' · ' + ext.toUpperCase() : '')
			return ext && ext !== row.title ? ext.toUpperCase() : row.media_type || '—'
		},
		usageLabel(u) {
			const type = { page: 'Page', news: 'News', faq: 'FAQ', business: 'Business' }[u.type] || u.type
			return u.via === 'featured' ? type + ' (featured)' : type
		},
		goTo(page) {
			this.page = page
			if (this.paged) this.load()
		},
		setPerPage(value) {
			this.perPageOpen = false
			if (value === this.perPage) return
			this.perPage = value
			try {
				localStorage.setItem('mc2.juniperClientDirectory.' + this.type_ + '.perPage', String(value))
			} catch (e) {
				// storage blocked — the choice just lasts for this visit
			}
			this.page = 1
			if (this.paged) this.load()
		},
		onOutsideClick(e) {
			if (this.perPageOpen && this.$refs.perPageWrap && !this.$refs.perPageWrap.contains(e.target)) {
				this.perPageOpen = false
			}
		},
		statusColor(status) {
			return { publish: 'green', active: 'green', draft: 'gray', future: 'blue', private: 'purple', archived: 'orange' }[status] || 'gray'
		},
		statusLabel(status) {
			return { publish: 'Published', active: 'Active', draft: 'Draft', future: 'Scheduled', private: 'Private', archived: 'Archived' }[status] || status || '—'
		},
		formatDate(value) {
			if (!value) return '—'
			const d = new Date(value)
			return isNaN(d) ? value : d.toLocaleDateString(undefined, { dateStyle: 'medium' })
		},
	},
}
</script>

<style scoped>
#clientDirectoryList {
	padding: 0 0 60px;
	margin-top: 0 !important;
}
.jc-view {
	width: 100%;
}
.hu-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: 1px solid #e0e1e1;
	border-radius: 999px;
	background: #ffffff;
	color: #64748b;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	cursor: pointer;
	text-decoration: none;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.hu-pill:hover:not(:disabled) {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
}
.hu-pill:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.hu-pill svg {
	font-size: 12px;
}
.jrd-intro {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 12px 14px;
	font-size: 13px;
	color: #4b5563;
}
.jrd-intro__hint {
	margin-top: 8px;
	font-size: 12px;
	color: #6b7280;
}
.cp-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	justify-content: space-between;
}
.cp-live-count {
	font-size: 13px;
	color: #6b7280;
}
.cp-filters {
	display: flex;
	gap: 8px;
}
.cp-input {
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	font-size: 13px;
	padding: 6px 10px;
	background: #fff;
	text-transform: capitalize;
}
.cp-input--search {
	width: 240px;
	text-transform: none;
}
.cp-input:focus {
	outline: none;
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}
.ada-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 1rem 1.1rem;
}
.act-empty {
	text-align: center;
	padding: 1.5rem 1rem;
	color: #8a909c;
	font-size: 0.9rem;
}
.act-table-wrap {
	overflow-x: auto;
	transition: opacity 0.15s ease;
}
.cp-dim {
	opacity: 0.55;
	pointer-events: none;
}
.act-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.act-table thead th {
	text-align: left;
	font-weight: 600;
	color: #6c757d;
	padding: 4px 10px 8px 0;
	border-bottom: 1px solid #dee2e6;
	white-space: nowrap;
}
.act-table tbody td {
	padding: 8px 10px 8px 0;
	border-bottom: 1px solid #f1f3f5;
	vertical-align: middle;
	color: #2b303b;
}
.act-table tbody tr:last-child td {
	border-bottom: none;
}
.act-table tbody tr:hover td {
	background: #f3f5f9;
}
.cp-row--muted .cp-title {
	color: #6b7280;
}
.cp-title {
	font-weight: 600;
}
.cp-sub {
	font-size: 11px;
	color: #6b7280;
}
.cp-muted {
	color: #6b7280;
}
.cp-cap {
	text-transform: capitalize;
}
.cp-nowrap,
.cp-date {
	white-space: nowrap;
}
.cp-date {
	color: #4b5563;
}
.cp-contact {
	font-size: 12px;
}
.act-pills {
	display: flex;
	background: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}
.act-pill {
	background: none;
	border: none;
	padding: 5px 14px;
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.15s ease;
}
.act-pill.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.act-pill:hover:not(.active) {
	color: #374151;
}
.cp-tab__count {
	margin-left: 6px;
	font-weight: 500;
	opacity: 0.75;
}
.cp-sortable {
	cursor: pointer;
	user-select: none;
}
.cp-sortable:hover {
	color: #111827;
}
.cp-file {
	max-width: 320px;
}
.cp-file__name {
	font-weight: 600;
	color: #2b303b;
	text-decoration: none;
	word-break: break-word;
}
a.cp-file__name:hover {
	color: #2b6cff;
	text-decoration: underline;
}
.cp-usage {
	font-size: 12px;
}
.cp-usage a {
	color: #2b6cff;
	text-decoration: none;
}
.cp-usage a:hover {
	text-decoration: underline;
}
.cp-usage__type {
	margin-right: 3px;
	color: #9ca3af;
}
.cp-pages {
	font-size: 12px;
}
.cp-page-link {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	margin-right: 8px;
	color: #2b6cff;
	text-decoration: none;
	white-space: nowrap;
}
.cp-page-link:hover {
	text-decoration: underline;
}
.cp-page-link--off {
	color: #6b7280;
}
.cp-pages-toggle {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 1px 8px;
	border: 1px solid #e6e8ec;
	border-radius: 999px;
	background: #fff;
	color: #374151;
	font-size: 11px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;
}
.cp-pages-toggle:hover {
	background: #f3f5f9;
}
.cp-pages-toggle svg {
	font-size: 9px;
}
.act-table tbody tr.cp-subrow td {
	background: #fafbfc;
	padding: 6px 10px 10px 12px;
}
.cp-page-list {
	margin: 0;
	padding: 0;
	list-style: none;
	font-size: 12px;
}
.cp-page-list li {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 3px 0;
}
.cp-page-list a {
	color: #2b6cff;
	font-weight: 600;
	text-decoration: none;
}
.cp-page-list a:hover {
	text-decoration: underline;
}
.cp-page-url {
	font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
	font-size: 11px;
	color: #9ca3af;
}
.cp-yes {
	color: #15803d;
	font-weight: 600;
}
.cp-pill {
	display: inline-block;
	padding: 2px 8px;
	margin-right: 4px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
}
.cp-pill--green { background: #dcfce7; color: #15803d; }
.cp-pill--gray { background: #f3f4f6; color: #4b5563; }
.cp-pill--blue { background: #dbeafe; color: #1d4ed8; }
.cp-pill--orange { background: #ffedd5; color: #c2410c; }
.cp-pill--purple { background: #ede9fe; color: #6d28d9; }
.act-actions {
	white-space: nowrap;
}
.act-row-btn {
	display: inline-block;
	background: none;
	border: none;
	color: #adb5bd;
	padding: 4px 6px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 13px;
	line-height: 1;
	transition: color 0.15s ease, background 0.15s ease;
}
.act-row-btn + .act-row-btn {
	margin-left: 2px;
}
.act-row-btn:hover {
	color: #2b303b;
	background: #f3f5f9;
}
/* Rows-per-page popover on the range label */
.cp-perpage {
	position: relative;
}
.cp-perpage__toggle {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 2px 6px;
	margin-left: -6px;
	border: none;
	border-radius: 6px;
	background: none;
	color: #8a909c;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	cursor: pointer;
}
.cp-perpage__toggle:hover,
.cp-perpage__toggle[aria-expanded='true'] {
	background: #f3f5f9;
	color: #2b303b;
}
.cp-perpage__caret {
	font-size: 9px;
}
.cp-perpage__menu {
	position: absolute;
	left: -6px;
	bottom: calc(100% + 6px);
	z-index: 10;
	min-width: 140px;
	padding: 4px;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	box-shadow: 0 6px 20px rgba(17, 24, 39, 0.12), 0 1px 3px rgba(17, 24, 39, 0.08);
}
.cp-perpage__title {
	padding: 4px 8px 6px;
	font-size: 11px;
	font-weight: 600;
	color: #8a909c;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}
.cp-perpage__opt {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 5px 8px;
	border: none;
	border-radius: 6px;
	background: none;
	color: #374151;
	font-size: 13px;
	text-align: left;
	cursor: pointer;
}
.cp-perpage__opt:hover {
	background: #f3f5f9;
}
.cp-perpage__opt.active {
	font-weight: 700;
	color: #111827;
}
.cp-perpage__opt svg {
	font-size: 10px;
}

.act-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.85rem 0 0.25rem;
	font-size: 0.82rem;
}
.act-muted {
	color: #8a909c;
}
.act-pagination__nav {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}
.act-page-link {
	background: none;
	border: 0;
	padding: 0 4px;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	color: #8a909c;
	cursor: pointer;
}
.act-page-link.active {
	color: #2b303b;
	font-weight: 700;
	cursor: default;
}
.act-page-link:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}
.act-page-link--arrow {
	font-size: 0.72rem;
}
.act-page-dots {
	color: #8a909c;
	padding: 0 2px;
	user-select: none;
}
</style>
